import { AppFooter } from "@/components/home/AppFooter";
import { AppTopNav, PAGE_CONTAINER_ID } from "@/components/home/AppTopNav";
import { FeatureCard } from "@/components/home/FeatureCard";
import { Seo } from "@/components/seo/Seo";
import { SignInWithStravaButton } from "@/components/strava/SignInWithStravaButton";
import { Button } from "@/components/ui/button";
import { FullScreenLoading } from "@/components/ui/loading";
import { useIsHydrated } from "@/hooks/useIsHydrated";
import { isSignedInAtom } from "@/state/auth.state";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useAtomValue } from "jotai";
import {
  BarChart3,
  Calendar,
  Map,
  RefreshCw,
  SlidersHorizontal,
  Table,
} from "lucide-react";
import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ROOT_PATH } from "./paths";

export function Home() {
  const isSignedIn = useAtomValue(isSignedInAtom);
  const hydrated = useIsHydrated();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (hydrated && isSignedIn === true && location.pathname === "/") {
      navigate("/app/charts", { replace: true });
    }
  }, [hydrated, isSignedIn, location.pathname, navigate]);

  if (hydrated && isSignedIn === undefined && location.pathname === "/") {
    return <FullScreenLoading title={<Trans>Loading...</Trans>} />;
  }

  if (hydrated && isSignedIn === true && location.pathname === "/") {
    return null;
  }

  return (
    <>
      <Seo
        title={t`Strava Statistics`}
        description={t`Analyze your Strava activities with powerful charts, calendars, tables, and heatmaps.`}
        canonicalPath="/"
      />
      <div
        id={PAGE_CONTAINER_ID}
        className="min-h-screen max-h-full overflow-y-auto"
      >
        {/* Header/Navigation */}
        <AppTopNav />

        {/* Hero Section */}
        <section className="relative overflow-hidden bg-gradient-to-br from-orange-50 via-white to-orange-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
          <div className="container mx-auto px-4 pb-20 pt-32 md:pb-32 md:pt-40">
            <div className="mx-auto max-w-4xl text-center">
              <h1 className="mb-6 text-5xl font-bold tracking-tight text-gray-900 dark:text-white md:text-6xl lg:text-7xl">
                <Trans>Unlock Your</Trans>{" "}
                <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">
                  <Trans>Activity Insights</Trans>
                </span>
              </h1>

              <p className="mb-10 text-xl text-gray-600 dark:text-gray-300 md:text-2xl">
                <Trans>
                  Advanced analytics, clear charts, and map-based visualizations
                  for your data.
                </Trans>
              </p>

              <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
                <div>
                  {isSignedIn ? (
                    <Button
                      asChild
                      size="lg"
                      className="bg-gradient-to-r from-orange-600 to-red-600 text-white shadow-lg hover:from-orange-500 hover:to-red-500 focus-visible:ring-orange-500"
                    >
                      <Link to={ROOT_PATH} className="flex items-center gap-2">
                        <BarChart3 className="h-5 w-5" />
                        <Trans>Go to dashboard</Trans>
                      </Link>
                    </Button>
                  ) : (
                    <SignInWithStravaButton />
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        {/* Features */}
        <section className="border-t border-gray-200 bg-gray-50/50 py-16 dark:border-gray-800 dark:bg-gray-900/20">
          <div className="container mx-auto px-4">
            <div className="mx-auto mb-10 max-w-3xl text-center">
              <h2 className="text-3xl font-semibold tracking-tight md:text-4xl">
                <Trans>Everything You Need to Analyze Your Activities</Trans>
              </h2>
              <p className="mt-3 text-lg text-gray-600 dark:text-gray-300">
                <Trans>
                  From detailed charts to interactive maps, Stratistics gives
                  you the tools to track what matters.
                </Trans>
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              <FeatureCard
                icon={<BarChart3 className="h-5 w-5" />}
                title={<Trans>Progress charts</Trans>}
                desc={
                  <Trans>
                    Distance, time, elevation —by week, month, and more.
                  </Trans>
                }
              />

              <FeatureCard
                icon={<Table className="h-5 w-5" />}
                title={<Trans>Activity table</Trans>}
                desc={
                  <Trans>Search, sort, and view all your activities.</Trans>
                }
              />

              <FeatureCard
                icon={<Calendar className="h-5 w-5" />}
                title={<Trans>Calendar view</Trans>}
                desc={<Trans>Spot streaks and recovery periods.</Trans>}
              />

              <FeatureCard
                icon={<Map className="h-5 w-5" />}
                title={<Trans>Maps</Trans>}
                desc={
                  <Trans>
                    Explore where you've been with map visualizations.
                  </Trans>
                }
              />

              <FeatureCard
                icon={<SlidersHorizontal className="h-5 w-5" />}
                title={<Trans>Global filters</Trans>}
                desc={
                  <Trans>
                    Filter all the data by activity types, dates and more.
                  </Trans>
                }
              />

              <FeatureCard
                icon={<RefreshCw className="h-5 w-5" />}
                title={<Trans>Automatic sync</Trans>}
                desc={<Trans>Your Strava data automatically syncs.</Trans>}
              />
            </div>
          </div>
        </section>

        {/* Footer */}
        <AppFooter />
      </div>
    </>
  );
}
